import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fmtTL } from "../../lib/format";

const TURKEY_CENTER = [39.0, 35.0];
const hasPos = (r) => Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)) && !(Number(r.latitude) === 0 && Number(r.longitude) === 0);

// Eksik silimi olan ATM kırmızı, tamamlanan yeşil pin (Leaflet varsayılan ikon dosyaları yerine renkli divIcon)
const pin = (missing) => L.divIcon({
  className: "",
  iconSize: [28, 36], iconAnchor: [14, 36], popupAnchor: [0, -34],
  html: `<svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.3 0 0 6.1 0 13.7 0 23.9 14 36 14 36S28 23.9 28 13.7C28 6.1 21.7 0 14 0z" fill="${missing ? "#dc2626" : "#059669"}" stroke="#fff" stroke-width="2"/><circle cx="14" cy="13.5" r="5" fill="#fff"/></svg>`,
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 13); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 14 });
  }, [map, points]);
  return null;
}

export default function AtmMap({ rows, showSub = true, canWipe = false, onWipe, height = "h-[520px]" }) {
  const located = useMemo(() => rows.filter(hasPos), [rows]);
  const points = useMemo(() => located.map((r) => [Number(r.latitude), Number(r.longitude)]), [located]);
  const noPos = rows.length - located.length;
  return (
    <div className="space-y-2" data-testid="atm-map">
      <div className="flex items-center gap-4 text-xs text-slate-600 flex-wrap" data-testid="atm-map-legend">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-600 inline-block" /> Eksik silim var (<b>{located.filter((r) => r.missing > 0).length}</b>)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-600 inline-block" /> Tamamlandı (<b>{located.filter((r) => !(r.missing > 0)).length}</b>)</span>
        {noPos > 0 && <span className="text-amber-700" data-testid="atm-map-nopos">{noPos} ATM'nin enlem/boylamı yok — haritada gösterilemiyor</span>}
      </div>
      <div className={`${height} w-full rounded-lg overflow-hidden border border-slate-200 relative z-0`}>
        <MapContainer center={TURKEY_CENTER} zoom={6} scrollWheelZoom className="h-full w-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={points} />
          {located.map((r) => (
            <Marker key={r.id} position={[Number(r.latitude), Number(r.longitude)]} icon={pin(r.missing > 0)}>
              <Popup>
                <div className="text-xs space-y-1 min-w-[200px]" data-testid={`atm-map-popup-${r.id}`}>
                  <div className="font-bold text-slate-900 text-sm">{r.branch_code} · {r.name}</div>
                  <div className="text-slate-600">{[r.city, r.district].filter(Boolean).join(" / ")}{r.project_name ? ` · ${r.project_name}` : ""}</div>
                  {r.address && <div className="text-slate-500">{r.address}</div>}
                  <div>Planlanan <b>{r.planned}</b> · Gerçekleşen <b className="text-sky-800">{r.actual}</b> · Eksik <b className={r.missing > 0 ? "text-red-700" : "text-emerald-700"}>{r.missing}</b></div>
                  {showSub && r.subcontractor_names?.length > 0 && <div className="text-slate-600">Taşeron: {r.subcontractor_names.join(", ")}{r.wipe_price ? ` · ${fmtTL(r.wipe_price)}/silim` : ""}</div>}
                  {canWipe && onWipe && <button type="button" onClick={() => onWipe(r)} className={`mt-1 w-full h-8 rounded-md text-white font-bold text-xs ${r.planned > 0 && r.missing === 0 ? "bg-amber-600" : "bg-sky-700"}`} data-testid={`atm-map-wipe-${r.id}`}>{r.planned > 0 && r.missing === 0 ? "Telafi Temizliği" : "Silim Yap"}</button>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
